import { defineStore } from 'pinia';
import { ref, reactive, computed, watch } from 'vue';
import LZString from 'lz-string';

export const useSurveyStore = defineStore('survey', () => {
  const mode = ref('survey'); // survey, checklist
  const step = ref(0);
  const totalSteps = 7;

  const surveyResult = reactive({
    v: '1', // futureproofing
    name: '',
    yearsToRetirement: null,
    spendTime: [],
    feel: null,
    prepared: null,
    livingArrangements: null,
    products: [],
    checkboxes: Array.from({ length: 16 }).map((_, index) => (index === 0))
  });

  const optionsVersioned = reactive({
    1: {
      yearsToRetirement: [
        {
          label: '0-2 years',
          value: '1'
        },
        {
          label: '3-5 years',
          value: '2'
        },
        {
          label: '6-10 years',
          value: '3'
        },
        {
          label: '11-20 years',
          value: '4'
        },
        {
          label: '21+ years',
          value: '5'
        },
        {
          label: 'Already retired',
          value: '6'
        },
        {
          label: 'Not sure',
          value: '7'
        }
      ],
      spendTime: [
        {
          label: 'Spend more time with my family and friends',
          value: '1'
        },
        {
          label: 'To do home improvements/renovations',
          value: '2'
        },
        {
          label: 'To move house (in the UK)',
          value: '3'
        },
        {
          label: 'Go on a dream holiday(s) and/or spend time relaxing',
          value: '4'
        },
        {
          label: 'To have a part time job and/or volunteer',
          value: '5'
        },
        {
          label: 'To relocate (outside of the UK)',
          value: '6'
        },
        {
          label: 'To ensure I have money to leave to family after I\'m gone',
          value: '7'
        },
        {
          label: 'To relax and enjoy my free time',
          value: '8'
        },
        {
          label: 'Not sure',
          value: '9'
        }
      ],
      feel: [
        {
          label: 'Great',
          value: '1',
          image: '/dist/images/great.png'
        },
        {
          label: 'Good',
          value: '2',
          image: '/dist/images/good.png'
        },
        {
          label: 'Neutral',
          value: '3',
          image: '/dist/images/neutral.png'
        },
        {
          label: 'Anxious',
          value: '4',
          image: '/dist/images/anxious.png'
        },
        {
          label: 'Sad',
          value: '5',
          image: '/dist/images/sad.png'
        }
      ],
      prepared: [
        {
          label: 'Completely prepared',
          value: '1',
          image: '/dist/images/great.png'
        },
        {
          label: 'Somewhat prepared',
          value: '2',
          image: '/dist/images/neutral.png'
        },
        {
          label: 'Not prepared',
          value: '3',
          image: '/dist/images/sad.png'
        }
      ],
      livingArrangements: [
        {
          label: 'own a home, which is fully paid off',
          value: '1'
        },
        {
          label: 'own a home, the mortgage is close to paying off',
          value: '2'
        },
        {
          label: 'own a home, the mortgage is not close to paying off',
          value: '3'
        },
        {
          label: 'rent',
          value: '4'
        },
        {
          label: 'part own, part rent',
          value: '5'
        },
        {
          label: 'live rent free',
          value: '6'
        }
      ],
      products: [
        {
          label: 'Life insurance',
          value: '1',
          hint: 'a type of policy that protects your loved ones with financial support if you die'
        },
        {
          label: 'Over 50s life insurance',
          value: '2',
          hint: 'life insurance specifically for people aged 50+ '
        },
        {
          label: 'Will',
          value: '3',
          hint: 'a legal document that communicates your final wishes in relation to your assets'
        },
        {
          label: 'Lasting power of attorney (LPA)',
          value: '4',
          hint: 'a legal document that lets you appoint someone you trust to make decisions for you, in case you ever become unable to make these yourself'
        },
        {
          label: 'Lifetime mortgage',
          value: '5',
          hint: 'accessing the equity (cash) tied up in your home. This is sometimes known as equity release'
        },
        {
          label: 'Funeral plan',
          value: '6',
          hint: 'allows you to pay upfront for your funeral so your relatives don\'t have to cover the costs themselves'
        },
        {
          label: 'None of these',
          value: '7',
          hint: ''
        },
      ]
    },
  });

  const options = computed(() => {
    return optionsVersioned[surveyResult.v];
  });

  function nextStep(i = null) {
    if (i === null) {
      if (surveyResult.yearsToRetirement === '6' && step.value === 4) {
        step.value = 6;
        return;
      }
      step.value += 1;
    } else if (i === -1) {
      if (surveyResult.yearsToRetirement === '6' && step.value === 6) {
        step.value = 4;
        return;
      }
      step.value -= 1;
    } else {
      step.value = i;
    }
  }

  const currentStep = computed(() => {
    return step.value;
  });

  const checkboxOptions = computed(() => [
    {
      label: 'Complete the retirement ready questionnaire',
      hint: 'It\'s great that you have started to think about retirement, taking the first steps can sometimes be the hardest',
    },
    {
      label: 'Speak to loved ones about what a happy retirement looks like for you',
      hint: 'Retirement is not always just a personal decision, it may also have a significant impact on your family and loved ones. Consider making time to discuss your hopes, wishes and expectations for a happy retirement.'
    },
    {
      label: 'Check your State Pension',
      hint: 'Use the <a href="https://www.gov.uk/check-state-pension?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">government online service</a> to find out how much State Pension you could get, when you can get it and how to increase it, if you can.'
    },
    {
      label: 'Find out when you can retire',
      hint: '<a href="https://www.gov.uk/state-pension-age?step-by-step-nav=c0ff9296-e91e-40d1-97bd-008026e90426" target="_blank">Check your State Pension age</a> to find out when you can start receiving your State Pension'
    },
    {
      label: 'Track any lost or forgotten work place pensions',
      hint: 'UK pension savers are being encouraged to take action to trace their lost pension pots. Consider using the <a href="https://www.gov.uk/find-pension-contact-details" target="_blank">Pension Tracing Service</a> to help track down any lost or forgotten workplace pensions you may have.'
    },
    {
      label: 'Consider writing a will',
      hint: 'A will helps to ensure that your assets are distributed according to your wishes and to help provide for the wellbeing of your loved ones after your death. You can <a href="https://les.coop.co.uk/wills?organisation=retirement-checklist&utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">start your will online</a> with Co-op Legal Services, then they call you to make sure the will is right for you and discuss anything you\'re not sure of.'
    },
    {
      label: 'Review your will and other estate planning',
      hint: 'Once you\'ve made a will, it\'s important to review it regularly to ensure it still reflects your wishes. You can also get additional protection for certain assets, either after your death or during your lifetime. Book a <a href="https://www.co-oplegalservices.co.uk/partners/retirement-checklist-free-legal-review/?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">complimentary wills and estate planning review</a>.',
      hide: !surveyResult.products.includes('3')
    },
    {
      label: 'Find out more about life insurance',
      hint: 'Life insurance is cover designed to pay out money if you pass away. This money can then be used for things such as paying off a mortgage and other debts or to financially help your loved ones. You can <a href="https://www.coop.co.uk/insurance/life-cover?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">get a quote</a> online with Co-op Insurance.'
    },
    {
      label: 'Choose someone to make decisions about your finances if you become unable to',
      hint: 'A lasting power of attorney (LPA) is a legal document that allows you to appoint someone to make decisions on your behalf if you become unable to make decisions for yourself due to mental or physical incapacity. You can <a href="https://www.co-oplegalservices.co.uk/making-a-will/lasting-powers-of-attorney/start-your-lpa/?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">start your LPA</a> online with Co-op Legal Services, or find out more <a href="https://www.co-oplegalservices.co.uk/making-a-will/lasting-powers-of-attorney/?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">here</a>.'
    },
    {
      label: 'Work out your funeral arrangements',
      hint: 'Think about whether this will be paid for by family or friends, your savings, life insurance or a funeral plan. To find out more, visit <a href="https://www.coop.co.uk/funeralcare/funeral-plans?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">Co-op Funeralcare</a>.'
    },
    {
      label: 'Decide if your home will play a part (lifetime mortgage)',
      hint: 'Equity in your home could be released tax free to help support your retirement goals and aspirations.',
      hide: surveyResult.livingArrangements !== '1' && surveyResult.livingArrangements !== '2' && surveyResult.livingArrangements !== '3'
    },
    {
      label: 'Get involved in your community',
      hint: 'Use your time, skills and enthusiasm to make a direct impact in your community and beyond.'
      // hint: '<a href="https://co-operate.coop.co.uk/?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">Co-operate</a> can connect you with groups that need help. Use your time, skills and enthusiasm to make a direct impact in your community and beyond.'
    },
    {
      label: 'Find new and existing hobbies you wish to pursue',
      hint: 'Hobbies not only fill up your time but can also keep you physically and mentally healthy. The more hobbies you have, the wider your social circle can become.'
    },
    {
      label: 'Set retirement goals',
      hint: 'Having goals in place will help you focus your mind and give you something to aim towards. Think about what you want to achieve, write it down, and make plans for the next year and beyond.'
    },
    {
      label: 'Explore working in retirement',
      hint: 'Reaching State Pension age doesn\'t always mean the end of working life. You can continue working and still receive your State Pension without having to pay <a href="https://www.gov.uk/tax-national-insurance-after-state-pension-age/stopping-paying-national-insurance" target="_blank">National Insurance</a>. If working in retirement is something you are interested in you can explore our current Co-op job vacancies <a href="https://jobs.coop.co.uk/?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">here</a>.'
    },
    {
      label: 'Start your own business',
      hint: 'According to research by Age UK, the number of self-employed people aged 65 and over has more than doubled in the past five years. Look into <a href="https://www.gov.uk/set-up-business?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">how to set up your own business</a> and ensure you are protected with <a href="https://coop.co.uk/insurance/business-insurance?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">business insurance, through Co-op.</a>',
      hide: true
    },
    {
      label: 'Check your national insurance payments to see if any years are missing',
      hint: 'If you have gaps in your <a href="https://www.gov.uk/check-national-insurance-record?utm_source=retirement+checklist&utm_medium=referral&utm_campaign=life+planning" target="_blank">national insurance (NI) record</a> that are preventing you from receiving a full state pension, it\'s worth checking if you can pay voluntary contributions to fill these',
      hide: true
    }
  ].map((option, index) => {
    return {
      ...option,
      index
    };
  }));

  const encodeUrlParams = () => {
    return LZString.compressToEncodedURIComponent(JSON.stringify(surveyResult));
  };

  const decodeUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const decoded = LZString.decompressFromEncodedURIComponent(params.get('id'));
    if (decoded) {
      Object.assign(surveyResult, JSON.parse(decoded));
      mode.value = 'checklist';
    }
  };

  watch(step, () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });

  watch(() => surveyResult.products, () => {
    if (mode.value === 'checklist') {
      return;
    }
    // if will is selected in products, check the corresponding checkbox

    if (surveyResult.products.includes('3')) { // will
      surveyResult.checkboxes[5] = true;
    }

    if (surveyResult.products.includes('1') || surveyResult.products.includes('2')) { // life insurance
      surveyResult.checkboxes[7] = true;
    }

    if (surveyResult.products.includes('4')) { // LPA
      surveyResult.checkboxes[8] = true;
    }

    if (surveyResult.products.includes('5')) { // Lifetime Mortgage
      surveyResult.checkboxes[10] = true;
    }

    if (surveyResult.products.includes('6')) { // Funeral plan
      surveyResult.checkboxes[9] = true;
    }
  });

  watch(() => surveyResult.checkboxes, () => {
    if (mode.value === 'survey') {
      return;
    }

    const currentUrl = window.location.href.split('?')[0].split('#')[0];
    const urlParameter = `?id=${encodeUrlParams()}`;
    window.history.pushState({}, '', currentUrl + urlParameter);
  }, { deep: true });

  watch(() => surveyResult.spendTime, (newValues, oldValues) => {
    // if user checks value '9' uncheck all others. Also if user checks any other value, uncheck value '9'
    if (oldValues.length === 0) {
      return;
    }
    // if arrays are the same, do nothing
    if (JSON.stringify(newValues) === JSON.stringify(oldValues)) {
      return;
    }

    if (newValues.includes('9') && !oldValues.includes('9')) {
      surveyResult.spendTime = ['9'];
    } else if (oldValues.includes('9') && newValues.length > 1) {
      surveyResult.spendTime = newValues.filter((value) => value !== '9');
    }
  });

  return {
    mode,
    step,
    totalSteps,
    surveyResult,
    options,
    optionsVersioned,
    checkboxOptions,

    currentStep,

    nextStep,
    encodeUrlParams,
    decodeUrlParams,
  };
});
